import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BuyCreditsModal = ({ isOpen, onClose }: BuyCreditsModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleOk = () => {
    onClose();
    navigate("/package-list");
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50">
        {/* Modal Content */}
        <div className="modal bg-white-500 lg:rounded-[1.5vw] rounded-3xl 2xl:p-[2vw] md:p-8 p-6 2xl:max-w-[35vw] md:max-w-[600px] max-w-[90%] w-full relative animate-[slideUp_0.3s_ease-out] lg:border-[0.2vw] border-3 border-primary auth-page overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)] hide-scroll">
          <div className="flex lg:gap-[1vw] gap-2 items-start justify-between w-full lg:mb-[1vw] mb-3">
            <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/tight text-2xl/6 font-semibold text-primary-orange">
              {t("createGame.error.noAvailableGamesInYourAccount")}
            </h1>
            {/* Close Button */}
            <button
              onClick={onClose}
              className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 outline-none"
            >
              <svg
                className="w-full h-full"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                  fill="#333938"
                />
              </svg>
            </button>
          </div>

          {/* Divider */}
          <div className="text-center lg:mb-[1.042vw] md:mb-6 mb-4 landscape:max-lg:mb-4">
            <hr className="border-0 2xl:h-[0.053vw] h-px w-full 2xl:mb-[1vw] md:mb-4 mb-3 2xl:mt-[0.5vw] mt-2 [background:linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />
          </div>

          {/* Message */}
          <div className="lg:space-y-[0.833vw] space-y-4 lg:mb-[1.042vw] md:mb-6 mb-4">
            <p className="2xl:text-[1.12vw]/tight text-lg/tight font-bold text-black-300 text-center">
              {t("createGame.error.noAvailableGamesInYourAccountDescription")}
            </p>
          </div>

          {/* Action Button */}
          <div className="flex justify-center">
            <button
              onClick={handleOk}
              className="2xl:min-w-[12vw] min-w-[200px] w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center hover:-translate-y-0.5 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
            >
              <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full">
                <span className="skew-x-[10deg] block text-white">
                  {t("createGame.buyGames") || "OK"}
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default BuyCreditsModal;
